import { Link } from 'react-router-dom';
import { Clock, Heart, UtensilsCrossed } from 'lucide-react';
import { photoUrl, photoSrcSet } from '@/lib/api';
import { formatMinutes, totalMinutes } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Category, RecipeSummary } from '@shared/schemas';

interface Props {
  recipe: RecipeSummary;
  categoriesById: Map<number, Category>;
  /** sizing class for the card — a height (h-44) or aspect ratio (aspect-[3/4]). */
  className: string;
  favourite: boolean;
  /** The likely LCP image: load it eagerly at high priority instead of lazily. */
  priority?: boolean;
}

// Immersive overlay card: full-bleed photo, frosted glass category tag, title +
// time over a gradient. The favourite heart shows only when this recipe is
// favourited (passed in by the parent, gated to logged-in).
export default function RecipeCard({
  recipe,
  categoriesById,
  className,
  favourite,
  priority = false,
}: Props) {
  const url = photoUrl(recipe.photo_key);
  const total = formatMinutes(totalMinutes(recipe.prep_minutes, recipe.cook_minutes));
  const categories = recipe.category_ids
    .map((id) => categoriesById.get(id))
    .filter((c): c is Category => !!c);

  return (
    <Link
      to={`/recipe/${recipe.slug}`}
      className={cn('group relative block overflow-hidden rounded-2xl bg-accent-soft', className)}
    >
      {url ? (
        <img
          src={url}
          srcSet={photoSrcSet(recipe.photo_key)}
          sizes="(min-width: 1024px) 250px, (min-width: 640px) 33vw, 50vw"
          alt=""
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-text-muted/40">
          <UtensilsCrossed size={28} />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />

      {categories.length > 0 && (
        // Show every category, but only those that fit on one row (#5): the row is
        // exactly one chip tall with overflow hidden, so chips that wrap to a
        // second line are clipped — no partial chips, no "+N".
        <div
          className={cn(
            'absolute left-2.5 top-2.5 flex h-6 flex-wrap gap-1 overflow-hidden',
            favourite ? 'right-11' : 'right-2.5',
          )}
        >
          {categories.map((c) => (
            <span
              key={c.id}
              className="inline-flex h-6 items-center whitespace-nowrap rounded-full bg-white/80 px-2.5 text-[11px] font-bold text-text backdrop-blur-md"
            >
              {c.name}
            </span>
          ))}
        </div>
      )}
      {favourite && (
        <span className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/80 backdrop-blur-md">
          <Heart size={15} className="fill-heart text-heart" />
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <h3 className="text-sm font-bold leading-tight">{recipe.title}</h3>
        {total && (
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-white/85">
            <Clock size={11} /> {total}
          </p>
        )}
      </div>
    </Link>
  );
}
